<template>
<div class="vessel-tracks">
  <div class="header">
    Selected vessel tracks
  </div>

  <vue-perfect-scrollbar class="vessel-tracks__items">
    <div v-for="(vessel, index) in internalVessels"
         :key="`vessel-${index}`"
         class="vessel-tracks__item" >
      <div class="title" @click="handleSelectVessel(vessel)">
        <div class="track-color"
             :style="{ backgroundColor: getPaletteColor(index) }">
        </div>
        {{ getVesselTitle(vessel) }}
      </div>
      <div class="actions">
        <b-button variant="link"
                  title="Remove the current track"
                  class="action-button remove-button"
                  @click="handleRemoveVessel(vessel)">
          <i class="fa fa-lg fa-remove"></i>
        </b-button>
      </div>
    </div>
  </vue-perfect-scrollbar>

  <div class="vessel-tracks__daterange">
    <date-range-picker-custom
      @input="handleDateRangeChanged"
      :startDate.sync="internalStartDate"
      :endDate.sync="internalEndDate"
      :options="{opens: 'right', ranges: getDatePickerRanges()}" />
  </div>

  <!-- show/hide tracking points switcher -->
  <div class="vessel-tracks__tracking-points">
    <div @click="$emit('update:show-points', !showPoints)" class="label">
      Show track points
    </div>
    <CSwitch :checked="showPoints"
             @update:checked="$emit('update:show-points', $event)"
             v-bind="{labelOn: 'on', labelOff: 'off'}"
             class="switch" color="dark" variant="outline" shape="pill" size="sm" />
  </div>

</div>
</template>

<script>
import cloneDeep from 'lodash-es/cloneDeep'
import getNested from 'lodash-es/get'
import moment from 'moment'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'

import { trackingPalette } from '@/shared/constants/colors'
import DateRangePickerCustom from '../widgets/DateRangePickerCustom'
import {
  getCurrentQuarterRange,
  getToday,
  getYearToDateRange
} from '@/shared/date-utils'

export default {
  name: 'vessel-track-management',

  components: {
    DateRangePickerCustom,
    VuePerfectScrollbar,
  },

  props: {
    startDate: {
      type: String,
      default: () => moment('2015-01-01').format('YYYY-MM-DD')
    },

    endDate: {
      type: String,
      default: () => moment().format('YYYY-MM-DD'),
    },

    vessels: {
      type: Array,
      default: () => [],
    },

    palette: {
      type: Array,
      default: () => trackingPalette,
    },

    showPoints: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      internalStartDate: this.startDate,
      internalEndDate: this.endDate,
      internalVessels: cloneDeep(this.vessels),
    };
  },

  mounted() {
    this.internalStartDate = this.startDate;
    this.internalEndDate = this.endDate;
    this.internalVessels = cloneDeep(this.vessels);
  },

  methods: {
    handleRemoveVessel(vessel) {
      this.internalVessels = this.internalVessels.filter(
        item => item.imo !== vessel.imo
      );
      this.applyFilter();
      this.$emit('vessel-removed', vessel);
    },

    handleSelectVessel(vessel) {
      this.$emit('vessel-selected', vessel);
    },

    handleDateRangeChanged() {
      this.applyFilter();
    },

    applyFilter() {
      const data = {
        dateRange: {
          startDate: this.internalStartDate,
          endDate: this.internalEndDate,
        },
        vessels: this.internalVessels,
      };

      this.$emit('input', data);
    },

    getPaletteColor(index) {
      return this.palette[index % this.palette.length];
    },

    getVesselTitle(vessel) {
      const name =  vessel.vesselName || getNested(vessel, 'static.name', '--');
      return `${name} (IMO: ${vessel.imo})`
    },

    getDatePickerRanges() {
      return {
        'Full period (2015-today)': [
          moment('2015-01-01').toDate(),
          getToday().toDate(),
        ],
        'QTD': getCurrentQuarterRange(),
        'YTD': getYearToDateRange(),
      };
    },
  },

  watch: {
    vessels() {
      this.internalVessels = cloneDeep(this.vessels);
    },
  },
}
</script>

<style scoped lang="scss">
  .vessel-tracks {
    padding: 10px 6px 10px 12px;
    border-radius: 5px;

    .header {
      font-size: 15px;
      text-align: center;
    }

    &__items {
      max-height: 100px;
      overflow-y: auto;
      padding-right: 10px;
    }

    &__item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 8px;
      padding: 0 10px 0 10px;

      .title {
        cursor: pointer;
        white-space: nowrap;
        font-size: 12px;
        display: flex;
        align-items: center;

        .track-color {
          width: 12px;
          height: 12px;
          margin-right: 5px;
        }
      }

      .actions {
        .action-button {
          padding: 0;
          margin: 0 0 0 8px;
          color: #8e8e8e;
          cursor: pointer;
        }

        .remove-button:hover {
          color: #c10808;
        }
      }
    }

    &__item:hover {
      background-color: rgba(120, 120, 120, 0.5);
    }

    &__daterange {
      margin-top: 10px;
    }

    &__tracking-points {
      margin-top: 10px;
      display: flex;
      align-items: center;
      justify-content: center;

      .label {
        padding-right: 10px;
        font-size: 14px;
        cursor: pointer;
      }
    }
  }
</style>
